import { POLARITY } from '../../../config/constants.js';

/**
 * Resonance — the polarity payoff (BULLET-HELL.md §D7).
 *
 * Act II's tone rule already let a matched bullet wash through the player
 * harmlessly. That is Ikaruga's mechanic with its economy deleted: absorbing
 * cost the player nothing and bought them nothing, so flipping attunement was
 * a defensive chore. In Ikaruga absorption is *income* — it charges a release
 * — which is why the player swims into matching fire rather than merely
 * surviving it.
 *
 * This is that income. Every absorbed bullet is a stack: stacks raise the
 * `multiplier` that prices the ultimate charge an absorb pays
 * (UltimateSystem), and every `stacksPerRelease` of them discharge as
 * `polarity:release` — the absorbed fire handed back as a homing volley
 * (Game._firePolarityRelease, which is the only place that holds both the
 * player's projectile pool and the enemy list).
 *
 * Modelled on GrazeChain deliberately, down to the decay/fade shape: the two
 * are the same kind of thing (a legible, on-the-spot price for standing where
 * the bullets are), and a player who has learned to read one readout should not
 * have to learn a second grammar for the other. Like the chain it is a pure
 * listener with no reach into anything, so one instance covers every
 * SpawningSystem in the run without knowing more than one exists.
 *
 * Also like the chain, taking a hit does NOT break it — for the same reason,
 * and one more: a mismatched bullet biting you is already priced at
 * +mismatchBonus damage, and deleting the resonance on top would charge twice
 * for one misread.
 */
export class Resonance {
  constructor(eventBus) {
    this.eventBus = eventBus;
    /** Absorbs in the chain currently running; 0 when it has lapsed. */
    this.count = 0;
    /** Absorbs banked toward the next release (0 .. stacksPerRelease - 1). */
    this.stacks = 0;
    /** Seconds left before the chain lapses. */
    this.remaining = 0;
    /** Longest chain this run — a lifetime stat (save.stats.bestResonance). */
    this.peak = 0;
    /** Absorbs this run, all chains — a lifetime stat (save.stats.bulletsAbsorbed). */
    this.total = 0;
    // Seconds of readout still owed after a chain lapses (POLARITY.readoutFade).
    this.fade = 0;
    // Absorbs per release. An instance field rather than a straight read of
    // POLARITY so a champion whose whole run is absorption can be handed a
    // shorter fuse (Tine's Attunement, set by Game from her signature) without
    // the chain learning that champions exist.
    this.stacksPerRelease = POLARITY.stacksPerRelease;

    eventBus.on('bullet:absorbed', ({ x = 0, y = 0 } = {}) => this.add(x, y));
  }

  /** 1 .. POLARITY.multiplierMax — what the CURRENT absorb is worth. */
  get multiplier() {
    return Math.min(1 + this.count * POLARITY.multiplierPerStack, POLARITY.multiplierMax);
  }

  /** 0..1 of the window left, for the decay bar under the readout. */
  get fraction() {
    return POLARITY.window > 0 ? Math.max(0, this.remaining) / POLARITY.window : 0;
  }

  /** 0..1 toward the next release — the half of the readout that matters most. */
  get releaseFraction() {
    return this.stacksPerRelease > 0
      ? Math.min(1, this.stacks / this.stacksPerRelease)
      : 0;
  }

  /** Whether there is anything to draw (a live chain, or one still fading). */
  get isVisible() {
    return this.count > 0 && (this.remaining > 0 || this.fade > 0);
  }

  /**
   * Counts one absorbed bullet. The multiplier is read AFTER the increment, so
   * the chain you are standing in prices the absorb that extends it — the same
   * order GrazeChain uses, and for the same reason (a readout that disagrees
   * with what the last one paid is worse than no readout).
   *
   * Release stacks are counted separately from the chain: they survive a lapsed
   * window on purpose. A chain measures how cleanly you read one volley; the
   * release is the reward for reading the tones at all, and losing banked
   * progress to a two-second gap between rings would make it feel arbitrary.
   * They are reset only by `reset()` (stage boundary, death, run end).
   */
  add(x = 0, y = 0) {
    this.count++;
    this.total++;
    this.remaining = POLARITY.window;
    this.fade = 0;
    if (this.count > this.peak) this.peak = this.count;
    this.eventBus.emit('polarity:absorbed', { count: this.count, multiplier: this.multiplier, x, y });

    this.stacks++;
    if (this.stacks >= this.stacksPerRelease) {
      this.stacks = 0;
      this.eventBus.emit('polarity:release', { x, y });
    }
  }

  update(dt) {
    if (dt <= 0) return;
    if (this.remaining > 0) {
      this.remaining -= dt;
      if (this.remaining > 0) return;
      // Overshoot spills into the fade, so one long tick ages the readout by
      // the same amount as the frames it stood in for.
      dt = -this.remaining;
      this.remaining = 0;
      this.fade = POLARITY.readoutFade;
    }
    if (this.fade > 0) {
      this.fade -= dt;
      if (this.fade <= 0) {
        this.fade = 0;
        this.count = 0;
      }
    }
  }

  /** Ends the chain outright, banked stacks included — a stage boundary, a death, a run end. */
  reset() {
    this.count = 0;
    this.stacks = 0;
    this.remaining = 0;
    this.fade = 0;
  }
}
